import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Modal } from '../../components/Modal';
import { Button } from '../../components/Button';
import { createTable, patchTable, ApiError, type CreateTableBody, type PatchTableBody } from '../../lib/api';
import { useToast } from '../../hooks/useToast';
import { PS, T } from '../../lib/theme';
import type { AdminTable } from '../../lib/types';

interface Props {
  open: boolean;
  /** null = create mode; truthy = edit mode for the given table */
  table: AdminTable | null;
  onClose: () => void;
  onSaved: () => void;
}

export function TableFormDialog({ open, table, onClose, onSaved }: Props) {
  const { push } = useToast();
  const [name, setName] = useState('');
  const [displayOrder, setDisplayOrder] = useState('');
  const [description, setDescription] = useState('');
  const [pageUrl, setPageUrl] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [imagesText, setImagesText] = useState('');
  const [busy, setBusy] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    if (table) {
      setName(table.name);
      setDisplayOrder(String(table.display_order));
      setDescription(table.description ?? '');
      setPageUrl(table.page_url ?? '');
      setHourlyRate(table.hourly_rate ?? '');
      setIsActive(table.is_active);
      setImagesText((table.images ?? []).join('\n'));
    } else {
      setName('');
      setDisplayOrder('');
      setDescription('');
      setPageUrl('');
      setHourlyRate('');
      setIsActive(true);
      setImagesText('');
    }
    setBusy(false);
  }, [open, table]);

  // Focus the name field on open instead of using the autoFocus prop.
  useEffect(() => {
    if (open) {
      nameRef.current?.focus();
    }
  }, [open]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const order = Number(displayOrder);
    if (!name.trim()) {
      push('error', 'Name is required.');
      return;
    }
    if (!Number.isFinite(order)) {
      push('error', 'Display order must be a number.');
      return;
    }
    const rate = hourlyRate.trim() === '' ? null : Number(hourlyRate);
    if (rate !== null && !Number.isFinite(rate)) {
      push('error', 'Hourly rate must be a number or empty.');
      return;
    }
    const images = imagesText
      .split('\n')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    setBusy(true);
    try {
      if (table) {
        const body: PatchTableBody = {
          name: name.trim(),
          display_order: order,
          description: description.trim() || null,
          page_url: pageUrl.trim() || null,
          hourly_rate: rate,
          is_active: isActive,
          images,
        };
        await patchTable(table.id, body);
        push('success', `Table #${table.id} updated.`);
      } else {
        const body: CreateTableBody = {
          name: name.trim(),
          display_order: order,
          description: description.trim() || null,
          page_url: pageUrl.trim() || null,
          hourly_rate: rate,
          is_active: isActive,
          images,
        };
        const created = await createTable(body);
        push('success', `Table #${created.id} created.`);
      }
      onClose();
      onSaved();
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={table ? `Edit table #${table.id}` : 'New table'}>
      <form onSubmit={(e) => void handleSubmit(e)} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Field label="Name">
          <input ref={nameRef} value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Display order">
          <input type="number" value={displayOrder} onChange={(e) => setDisplayOrder(e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Description (optional)">
          <input value={description} onChange={(e) => setDescription(e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Page URL (optional)">
          <input value={pageUrl} onChange={(e) => setPageUrl(e.target.value)} style={inputStyle} />
        </Field>
        <Field label="Hourly rate (optional)">
          <input type="number" step="0.01" value={hourlyRate} onChange={(e) => setHourlyRate(e.target.value)} style={inputStyle} />
        </Field>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          <span style={T.ui}>Active</span>
        </label>
        <Field label="Images (one URL per line)">
          <textarea
            value={imagesText}
            onChange={(e) => setImagesText(e.target.value)}
            rows={4}
            style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
          />
        </Field>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" disabled={busy}>
            {busy ? 'Saving…' : table ? 'Save changes' : 'Create table'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={T.label}>{label}</span>
      {children}
    </label>
  );
}

const inputStyle = {
  padding: '8px 10px',
  border: `1px solid ${PS.border}`,
  borderRadius: 3,
  minHeight: 40,
  fontFamily: "'Barlow',sans-serif",
  fontSize: 16,
};
